import { ReactNode, useLayoutEffect, useRef, useState } from "react";
import { SLIDE_HEIGHT, SLIDE_WIDTH } from "./PowerSlideDesign";

interface PowerSlideStageProps {
  children: ReactNode;
  background: string;
  testId?: string;
}

/**
 * Renders its children at the slide's true 1280x720 size and scales the whole
 * thing to fit the space available, the same trick Figma's rescale uses.
 *
 * Editing at design size rather than reimplementing the layout at editor size
 * is what makes fidelity cheap: every measurement in PowerSlideDesign is the
 * literal Figma value, and the type ramp scales with the slide instead of
 * needing a second set of sizes that would drift.
 *
 * The fit is contain, not width-led: a width-led slide overflows a short pane
 * and pushes the surrounding chrome out of view.
 */
export default function PowerSlideStage(props: PowerSlideStageProps) {
  const { children, background, testId } = props;
  const containerRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(0);

  useLayoutEffect(() => {
    const element = containerRef.current;
    if (!element) return;

    const measure = () =>
      setScale(
        Math.min(
          element.clientWidth / SLIDE_WIDTH,
          element.clientHeight / SLIDE_HEIGHT
        )
      );
    measure();

    // Not available in jsdom, so tests fall back to the initial measurement.
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={containerRef}
      data-testid={testId}
      className="flex h-full w-full items-center justify-center"
    >
      {/* Sized in editor pixels so the corner radius and shadow stay crisp -
          putting them on the scaled element would shrink them with it. */}
      <div
        className="overflow-hidden rounded-[6px] shadow-lg"
        style={{
          width: SLIDE_WIDTH * scale,
          height: SLIDE_HEIGHT * scale,
          backgroundColor: background,
          // Hidden until measured, so the slide never flashes at full size.
          visibility: scale > 0 ? "visible" : "hidden",
        }}
      >
        <div
          style={{
            width: SLIDE_WIDTH,
            height: SLIDE_HEIGHT,
            transform: `scale(${scale})`,
            transformOrigin: "top left",
          }}
        >
          {children}
        </div>
      </div>
    </div>
  );
}
